
import React from 'react';
import { BrowserRouter, Route, Routes, Outlet, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { LandingLayout } from '@/components/layout/LandingLayout';
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from '@/context/AuthContext';
import { SidebarProvider } from '@/context/SidebarContext';


// Import pages
import Index from '@/pages/Index';
import Profile from '@/pages/Profile';
import Places from '@/pages/places';
import BusinessDetails from '@/pages/BusinessDetails';
import Dashboard from '@/pages/Dashboard';
import Auth from '@/pages/Auth';
import UpdatePassword from '@/pages/UpdatePassword';
import NotFound from '@/pages/NotFound';
import Tasks from '@/pages/Tasks';
import Companies from '@/pages/Companies';
import CompanyDetails from '@/pages/CompanyDetails';
import NewCompany from '@/pages/NewCompany';
import EditCompany from '@/pages/EditCompany';
import Admin from '@/pages/Admin';
import Documentation from '@/pages/Documentation';
import Subscriptions from '@/pages/Subscriptions';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import SubscriptionReturn from '@/pages/SubscriptionReturn';
import Payment from '@/pages/Payment';
import LandingPage from '@/pages/LandingPage';
import Features from '@/pages/Features';
import PricingPage from '@/pages/Pricing';
import HowItWorks from '@/pages/HowItWorks';
import InviteRedirect from '@/pages/InviteRedirect';
import MailMerges from '@/pages/MailMerges';
import CampaignPlanner from '@/pages/CampaignPlanner';
import EnrichmentQueue from '@/pages/EnrichmentQueue';
import SharedLeads from '@/pages/SharedLeads';
import SharedWithMe from '@/pages/SharedWithMe';
import Unsubscribed from '@/pages/Unsubscribed';
import CompleteProfile from '@/pages/CompleteProfile';


// Import components for error boundary
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthErrorBoundary } from '@/components/AuthErrorBoundary';
import './App.css';

const App = () => {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
        <AuthProvider>
          <SidebarProvider>
            <AuthErrorBoundary>
              <BrowserRouter>
                <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/update-password" element={<UpdatePassword />} />
            <Route path="/invite/:token" element={<InviteRedirect />} />
            <Route path="/unsubscribed" element={<Unsubscribed />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
              
              {/* Landing page - self-contained with its own navbar/footer */}
              <Route path="/" element={<LandingPage />} />
              
              {/* Other landing pages with shared dark layout */}
              <Route element={<LandingLayout />}>
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/subscription/return" element={<SubscriptionReturn />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              </Route>
              
              {/* Documentation - available to both logged in and logged out users */}
              <Route path="/documentation" element={
                <AppLayout>
                  <Documentation />
                </AppLayout>
              } />
              
              {/* Protected routes with app layout */}
              <Route element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }>
                <Route element={<AppLayout>
                  <Outlet />
                </AppLayout>}>
                  <Route path="/app" element={<Index />} />
                  <Route path="/places" element={<Places />} />
                  <Route path="/leads/:id" element={<BusinessDetails />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/tasks" element={<Tasks />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/companies" element={<Companies />} />
                  <Route path="/companies/new" element={<NewCompany />} />
                  <Route path="/companies/:id" element={<CompanyDetails />} />
                  <Route path="/companies/:id/edit" element={<EditCompany />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/subscription" element={<Subscriptions />} />
                  <Route path="/payment" element={<Payment />} />
                  <Route path="/mail-merges" element={<MailMerges />} />
                  <Route path="/campaign-planner" element={<CampaignPlanner />} />
                  <Route path="/enrichment-queue" element={<EnrichmentQueue />} />
                  <Route path="/shared/:token" element={<SharedLeads />} />
                  <Route path="/shared-with-me" element={<SharedWithMe />} />
                  <Route path="/shared-with-me/:shareId" element={<SharedWithMe />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
            </AuthErrorBoundary>
          </SidebarProvider>
        </AuthProvider>
      </ThemeProvider>
  );
};

export default App;
